import { useEffect, useMemo, useRef, useState, useCallback, useContext } from "react";
import BreadCrumb from "../../../common/breadCrumb/BreadCrumb";
import { Button, COLORS, Icon, ICONS, Modal, Switch, Tab } from "abs-ui-components";
import MpParticulars from "./FeuMpParticulars/FeuMpParticulars";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import dayjs from "dayjs";
import { callApi, getMonitoringPlanFlags, getVesselById, useMonitoringPlanActions } from "../../../services/MonitoringPlanService";
import { API_ROUTES } from "./FeuApiRoutes/FeuApiRoutes";
import { createB1Payload } from "./FeuConstants";
import { tabs } from "./FeuConstants";
import FeuBasicData from "./FeuBasicData/FeuBasicData";
import FeuDataGaps from "./FeuDataGaps/FeuDataGaps";
import FeuManagement from "./FeuManagement/FeuManagement";
import FeuActivityData from "./FeuActivityData/FeuActivityData";
import FeuFurtherInformation from "./FeuFurtherInformation/FeuFurtherInformation";
import { emitToasterMsg } from "../../../services/toasterUtility";
import axiosHttp from "../../../services/interceptor";
import CloneModal from "../CloneModal";
import ResultSummaryModal from "../ResultSummaryModal";
import { FeatureType } from "../../../constants/FeatureToggles";
import { ClaimsContext } from "../../../services/ClaimsService";

const BASE_URL = process.env.REACT_APP_BASE_URL;
const FuelEuMonitoringPlan = ({ vesselMstId, shouldClone, setShouldClone }) => {
	const params = useParams();
	const [selectedTab, setActiveTab] = useState("MP PARTICULARS");
	const [monitoringData, setMonitoringData] = useState(null);
	const [loading, setLoading] = useState(true);
	const [allDropdownData, setAllDropdownData] = useState(null);
	const location = useLocation();
	const [mode, setMode] = useState("");
	const [onCreateVesselData, setOnCreateVesselData] = useState(null);
	const hasAutoSubmitted = useRef(false);
	const [showCloneModal, setShowCloneModal] = useState(false);
	const [cloneType, setCloneType] = useState("");
	const [selectedSections, setSelectedSections] = useState([]);
	const [selectedVessels, setSelectedVessels] = useState([]);
	const [confirmationChecked, setConfirmationChecked] = useState(false);
	const [hasFuelEuPlan, setHasFueleuPlan] = useState(mode === "Edit" ? true : false);
	const query = new URLSearchParams(location.search);
	//const {shouldClone,setShouldClone}=useContext(ClaimsContext);
	//const shouldClone = useRef(query.get("shouldClone") === "true");
	const [confirmDelete, setConfirmDelete] = useState(false);
	const handleDeleteBtnClick = () => {
		setConfirmDelete(true);
	};
	const [resultSummaryData, setResultSummaryData] = useState([]);
	const [showResultModal, setShowResultModal] = useState(false);
	const [sectionsList, setSectionsList] = useState([]);
	const { userHasFeature } = useContext(ClaimsContext);
	useEffect(() => {
		const fetchMonitoringDropdownData = async () => {
			try {
				const response = await axiosHttp.get(`${BASE_URL}api/MrvMonitoringPlan/alldropdowndata`);
				if (response?.data) {
					setAllDropdownData(response.data);
				}
			} catch (error) {
				console.error("Error fetching monitoring plan data", error);
			} finally {
				setLoading(false);
			}
		};

		fetchMonitoringDropdownData();
	}, []);

	useEffect(() => {
		const flagCheck = async () => {
			const flagsResponse = await getMonitoringPlanFlags(vesselMstId);
			const flags = flagsResponse?.data;
			if (flags) {
				setHasFueleuPlan(flags.hasFuelEuPlan);
				setMode(flags.hasFuelEuPlan ? "Edit" : "Create");
			}
		};
		flagCheck();
	}, []);

	const handleValidate = async (planType) => {
		try {
			const res = await callApi({
				method: "POST",
				url: API_ROUTES.FEU_Validate_plan("FuelEu", vesselMstId),
				data: {}
			});

			if (res?.isValid) {
				await fetchSections();
				emitToasterMsg("success", `FEU MP Clone`, "Validate successfully");
				setCloneType(planType);
				setShowCloneModal(true);
			}
			else {
				emitToasterMsg("error", `FEU MP Clone`, res?.errors || "Validate failed");
			}
		} catch (err) {
			emitToasterMsg("error", `FEU MP Clone`, err.errors || "Validate failed");
		}
	}
	const fetchSections = async () => {
		try {
			const res = await callApi({
				method: "GET",
				url: API_ROUTES.Get_FEU_Sections_By_PlanType("FuelEu")
			});

			// Store in your modal's list state
			setSectionsList(res.sections);

		} catch (error) {
			console.error("Failed to load sections", error);
			emitToasterMsg("error", "Sections", "Failed to load sections");
		}
	};

	const fetchMonitoringData = useCallback(async () => {
		try {
			//const cloneFlag = shouldClone.current;
			const response = await axiosHttp.get(`${BASE_URL}api/FuelEuMonitoringPlan/${vesselMstId}?hasClone=${shouldClone}`);
			if (shouldClone) {
				setShouldClone(false);
				//shouldClone.current = false;
				// const url = new URL(window.location.href);
				// url.searchParams.delete("shouldClone");
				// window.history.replaceState({}, "", url);
			}
			if (response?.data) {
				setMonitoringData(response?.data);
			}
		} catch (error) {
			console.error("Error fetching monitoring plan data", error);
		} finally {
			setLoading(false);
		}
	});

	useEffect(() => {
		if (mode === "Edit" && vesselMstId) {
			fetchMonitoringData();
		}
	}, [mode, vesselMstId]);

	useEffect(() => {
		if (hasFuelEuPlan) return;
		else {
			(async () => {
				try {
					const data = await getVesselById(vesselMstId);
					setOnCreateVesselData(data);
				} catch { }
			})();
		}
	}, [hasFuelEuPlan]);

	const handleSubmit = async (rawData, withToast = false) => {
		const payload = createB1Payload(rawData, "Create");
		const finalPayload = {
			...payload,
			shipType: allDropdownData?.shipTypes?.find((type) => type.code === payload?.shipType)?.code ?? null,
		};
		try {
			const res = await callApi({
				method: "POST",
				url: API_ROUTES.B1(vesselMstId),
				data: finalPayload,
			});
			if (res?.isSuccess) {
				if (typeof fetchMonitoringData === "function") {
					await fetchMonitoringData();
				}
			}
		} catch (err) {
			console.error("API Error:", err.errorMessage);
		}
	};

	/* 3. Auto‑submit exactly once in Create mode */
	useEffect(() => {
		if (mode === "Create" && vesselMstId && onCreateVesselData && !hasAutoSubmitted.current) {
			hasAutoSubmitted.current = true;
			handleSubmit(onCreateVesselData, false);
		}
	}, [mode, vesselMstId, onCreateVesselData]);

	const hasAutoSavedPartA = useRef(false);

	const autoCreatePartA = useCallback(async () => {
		if (!vesselMstId) return;

		const payload = {
			vesselMstId: Number(vesselMstId),
			createdDate: dayjs().toISOString(),
			status: "Created",
			version: 0,
			isActive: true,
		};

		try {
			const res = await callApi({
				method: "POST",
				url: API_ROUTES.A,
				data: payload,
			});

			if (res?.isSuccess) {
				const flagsResponse = await getMonitoringPlanFlags(vesselMstId);
				const flags = flagsResponse?.data;

				// Set only the one you need
				if (flags) {
					setHasFueleuPlan(flags.hasFuelEuPlan);
				}
				await fetchMonitoringData();
			}
		} catch (err) {
			console.error("Auto‑save Part‑A failed:", err.errorMessage);
		}
	}, [vesselMstId]);

	useEffect(() => {
		if (mode === "Create" && vesselMstId && !hasAutoSavedPartA.current) {
			hasAutoSavedPartA.current = true;
			autoCreatePartA();
		}
	}, [mode, vesselMstId, autoCreatePartA, fetchMonitoringData]);

	//Enable the download all required fileds entered
	const checkThetisEnable = async () => {
		if (!vesselMstId) return;
		try {
			const res = await callApi({
				method: "GET",
				url: API_ROUTES.GET_ENABLE_FEU_DOWNLOAD_XML(vesselMstId),
				data: {},
			});
			if (res?.isValid) {
				await handleDownloadXML();
			} else {
				emitToasterMsg("error", "XML", res?.errors);
			}
		} catch (err) {
			emitToasterMsg("error", "XML", err?.errors);
		}
	};

	//Download XML api
	const handleDownloadXML = async () => {
		if (!vesselMstId) return;
		try {
			const res = await callApi({
				method: "GET",
				url: API_ROUTES.GET_DOWNLOAD_FEU_FILE(vesselMstId),
				data: {},
				responseType: "blob",
			});
			if (res) {
				const url = window.URL.createObjectURL(new Blob([res]));
				const link = document.createElement("a");
				link.href = url;
				link.setAttribute("download", `Fuel EU-${vesselMstId}.xml`);
				document.body.appendChild(link);
				link.click();
				link.parentNode.removeChild(link);
				emitToasterMsg("success", "XML", "XML download successfully");
			}
		} catch (err) {
			console.error("Error downloading Fuel EU XML", err);
		}
	};

	//Download pdf api integration
	const handleDownloadPDF = async () => {
		if (!vesselMstId) return;
		try {
			const res = await callApi({
				method: "POST",
				url: `${API_ROUTES.GET_FEU_DOWNLOAD_PDF(vesselMstId)}?planType=fueleu`,
				data: {},
				responseType: "blob",
			});
			if (res) {
				const url = window.URL.createObjectURL(new Blob([res], { type: "application/pdf" }));
				const link = document.createElement("a");
				link.href = url;
				link.setAttribute("download", `Fuel EU-${vesselMstId}.pdf`);
				document.body.appendChild(link);
				link.click();
				link.parentNode.removeChild(link);
				emitToasterMsg("success", "PDF", "PDF downloaded successfully");
			}
		} catch (err) {
			console.error("Error downloading PDF", err);
			emitToasterMsg("error", "PDF", "Failed to download PDF");
		}
	};

	const breadcrumb = useMemo(
		() => [
			{ label: "My Fleet", url: "/my-fleet", navId: "myfleet_navId" },
			{
				label: "FuelEu Monitoring Plan",
				url: "",
				navId: "mrv_monitoring_plan_navId",
			},
		],
		[]
	);

	const tabChangeHandler = (event) => {
		setActiveTab(event);
	};
	const navigate = useNavigate();
	const { deleteFuelEuMonitoringPlan } = useMonitoringPlanActions(() => { });

	const handleDeleteClick = async () => {
		await deleteFuelEuMonitoringPlan(vesselMstId);
		navigate("/plan-verification");
	};

	return (
		<>
			<div className="mb-2 d-flex justify-content-between details-container ml-4">
				<div className="ml-auto d-flex">
					<>
					{userHasFeature?.(FeatureType.FEUClone) && (
						<Button
							className="mr-2"
							onClick={() => {
								handleValidate("FEU")
							}}
						>
							FEU MP Clone
						</Button>
					)}
						<Button startIcon={<Icon icon={ICONS.DOWNLOAD} />} className="mr-2" onClick={checkThetisEnable}>
							Download XML
						</Button>
						<Button startIcon={<Icon icon={ICONS.DOWNLOAD} />} className="mr-2" onClick={handleDownloadPDF}>
							Download PDF
						</Button>
						<Button
							startIcon={loading ? <Icon icon="loader" spin /> : <Icon icon="delete" />}
							className="mr-2"
							// disabled={mode !== "Edit" || loading}
							onClick={handleDeleteBtnClick}
						>
							Delete Monitoring Plan
						</Button>
					</>
				</div>
			</div >
			<div className=" ml-4">
				<Switch links={tabs} selected={selectedTab} onChange={tabChangeHandler} />

				{/* Tab Content */}
				{selectedTab === "MP PARTICULARS" && (
					<MpParticulars mpParticularsData={monitoringData?.fuelEuPartASection} refetchMonitoringData={fetchMonitoringData}
						vesselMstId={vesselMstId}
					/>
				)}

				{selectedTab === "BASIC DATA" && (
					<FeuBasicData
						data={monitoringData?.fuelEuPartBSection}
						onCreateVesselData={onCreateVesselData}
						mode={mode}
						dropdownData={allDropdownData}
						refetchMonitoringData={fetchMonitoringData}
						vesselMstId={vesselMstId}
					/>
				)}

				{selectedTab === "ACTIVITY DATA" && (
					<FeuActivityData data={monitoringData?.fuelEuPartCSection} dropdownData={allDropdownData} refetchMonitoringData={fetchMonitoringData}
						vesselMstId={vesselMstId} />
				)}

				{selectedTab === "DATA GAPS" && (
					<FeuDataGaps
						FeuDataGapsData={monitoringData?.fuelEuPartDSection}
						dropdownData={allDropdownData}
						refetchMonitoringData={fetchMonitoringData}
						vesselMstId={vesselMstId}
					/>
				)}

				{selectedTab === "MANAGEMENT" && (
					<FeuManagement data={monitoringData?.fuelEuPartESection} dropdownData={allDropdownData} refetchMonitoringData={fetchMonitoringData}
						vesselMstId={vesselMstId} />
				)}

				{selectedTab === "FURTHER INFORMATION" && (
					<FeuFurtherInformation
						data={monitoringData?.fuelEuPartFSection}
						dropdownData={allDropdownData}
						refetchMonitoringData={fetchMonitoringData}
						vesselMstId={vesselMstId}
					/>
				)}
			</div>

			<div className="confirmation-modal-container">
				<Modal
					isOpen={confirmDelete}
					modalHeader
					padding={"70px"}
					headerHeight={"50px"}
					showFooter
					onCloseClickListener={() => {
						setConfirmDelete(false);
					}}
					footer={
						<div className="d-flex justify-content-end fixed-action-btns p-4">
							<Button
								className="modal-delete-btn"
								onClick={handleDeleteClick}
								primaryColor={COLORS.RED.DARK}
								primaryBackgroundColor={COLORS.RED.LIGHT}
							>
								Delete
							</Button>
							<Button
								className="modal-cancel-btn"
								onClick={() => {
									setConfirmDelete(false);
								}}
							>
								Cancel
							</Button>
						</div>
					}
				>
					<div>Are you sure you want to delete this Fuel EU monitoring plan ?</div>
				</Modal>
			</div>
			<div className="seemp-clone-modal-popup">
				<CloneModal
					isOpen={showCloneModal}
					headerTitle={"FEU MP Clone"}
					onClose={() => {
						setShowCloneModal(false);
						setSelectedSections([]);
						setSelectedVessels([]);
						setConfirmationChecked(false);
					}}
					vesselMstId={vesselMstId}
					planType={"FuelEu"}
					sectionsList={sectionsList || []}
					// vesselsList={allDropdownData?.vessels || []}

					selectedSections={selectedSections}
					setSelectedSections={setSelectedSections}

					selectedVessels={selectedVessels}
					setSelectedVessels={setSelectedVessels}

					confirmationChecked={confirmationChecked}
					setConfirmationChecked={setConfirmationChecked}

					onClone={async () => {
						const payload = {
							"planType": "FuelEu",
							"vesselMstId": vesselMstId,
							"cloningOptionIds": [
								...selectedSections
							],
							"targetVesselMstIds": selectedVessels?.map(v => v.vesselMstId),
						}
						// API Call Here
						try {
							const res = await callApi({
								method: "POST",
								url: API_ROUTES.CLONE_FEU(),
								data: payload
							});

							if (res?.isSuccess) {
								emitToasterMsg("success", `FEU MP Clone`, "Cloned successfully");
								setShowCloneModal(false);
								setSelectedSections([]);
								setSelectedVessels([]);
								setConfirmationChecked(false);
								setShowResultModal(true);
								setResultSummaryData(res?.cloneSummaries || []);
							}
							else {
								emitToasterMsg("error", `FEU MP Clone`, "Clone failed");
							}
						} catch (err) {
							emitToasterMsg("error", `FEU MP Clone`, "Clone failed");
						}
					}}
				/>

			</div>
			<div className="clone-result-summary-modal-popup">
				<ResultSummaryModal
					isOpen={showResultModal}
					onClose={() => setShowResultModal(false)}
					resultSummaryData={resultSummaryData || []}
					headerTitle={"FEU Clone - Result Summary"}
					subHeaderTitle={"Clone result for selected target vessels"}
					successMsg={"FEU"}
				/>
			</div>
		</>
	);
};

export default FuelEuMonitoringPlan;
